import { useEffect, useState } from "react"
import { FileText } from "lucide-react"
import { SettingsCard } from "@/app/components/SettingsCard"
import {
  Field,
  Input,
  ErrorBanner,
  SuccessBanner,
  formatApiError,
} from "@/app/components/FormPrimitives"
import { primaryButton } from "@/app/lib/buttonStyles"
import { useAssignorSettings, useUpdateAssignorSettings } from "@/app/api/assignments"
import type { ClientTheme } from "@/app/lib/themes"

const blank = { legal_name: "", address: "", signer: "", signer_title: "" }

// The client's own block printed on generated assignments (Assignments
// module). Owners and superadmins only — the page itself is manage-gated.
export function AssignorSettingsCard({ theme }: { theme: ClientTheme }) {
  const { data } = useAssignorSettings()
  const update = useUpdateAssignorSettings()
  const [form, setForm] = useState(blank)

  useEffect(() => {
    if (!data) return
    setForm({
      legal_name: data.legal_name ?? "",
      address: data.address ?? "",
      signer: data.signer ?? "",
      signer_title: data.signer_title ?? "",
    })
  }, [data])

  const set = (key: keyof typeof blank) => (value: string) => {
    update.reset()
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    update.mutate(form)
  }

  return (
    <SettingsCard
      icon={<FileText size={17} strokeWidth={1.75} />}
      title="Assignor Details"
      subtitle="Printed on every assignment generated from the Lease Schedule"
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
          <Field label="Legal Name" theme={theme}>
            <Input value={form.legal_name} onChange={set("legal_name")} theme={theme} />
          </Field>
          <Field label="Address" theme={theme}>
            <Input
              value={form.address}
              onChange={set("address")}
              placeholder="6701 Broadway Extension, Suite 310, Oklahoma City, OK 73116"
              theme={theme}
            />
          </Field>
          <Field label="Default Signer" theme={theme}>
            <Input value={form.signer} onChange={set("signer")} theme={theme} />
          </Field>
          <Field label="Signer Title" theme={theme}>
            <Input value={form.signer_title} onChange={set("signer_title")} theme={theme} />
          </Field>
        </div>
        {update.isError && <ErrorBanner theme={theme} message={formatApiError(update.error)} />}
        {update.isSuccess && <SuccessBanner theme={theme} message="Assignor details saved." />}
        <div className="flex justify-end mt-5">
          <button
            type="submit"
            disabled={update.isPending}
            className="btn"
            style={{ ...primaryButton(theme), opacity: update.isPending ? 0.6 : 1 }}
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </SettingsCard>
  )
}
